import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit, output, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PaymentMethods, ProcessorName } from '@cargos/sprintpay-models';
import { AdyenAdvancedComponent, AdyenComponentResponse } from '@cargos/sprintpay-ui';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { PaymentMethodsHandlerService } from '../../modules/cart/lib-cart/shared/services/payment-methods-handler.service';
import { CustomerService, PaymentMethodsService } from '../../services';
import { FormPersonalComponent } from '../form-personal/form-personal.component';
import { CreditCardFormMaterialModule } from './credit-card-form-material.modules.module';
import { CreditCardFormGroupType } from './models/credit-card-form';
import { CreditCardService } from './services/credit-card.service';

@Component({
    standalone: true,
    selector: 'app-sprintpay-credit-card-form',
    templateUrl: './credit-card-form.component.html',
    styleUrls: ['./credit-card-form.component.scss'],
    providers: [CreditCardService],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        CreditCardFormMaterialModule,
        AdyenAdvancedComponent,
        FormPersonalComponent,
        MatProgressSpinnerModule,
    ],
})
export class CreditCardFormComponent implements OnInit, OnDestroy {
    private unsubscribe$: Subject<void>;
    public processorName: ProcessorName;
    public isLoading: boolean = true;
    public isLoadingForm: boolean = false;
    public emailUser: string = '';
    public readonly adyenCardAdded = output<{
        adyenResponse: AdyenComponentResponse;
        creditCardFormGroup?: CreditCardFormGroupType;
    } | null>();
    public creditCardFormGroup: CreditCardFormGroupType;
    public isGuest: boolean = false;
    public isPersonalFormValid: boolean = false;

    @Input() addPersonalForm = true;
    @ViewChild('personalInfoForm', { static: false }) personalInfoForm?: ElementRef;

    constructor(
        private customerService: CustomerService,
        private paymentMethodsService: PaymentMethodsService,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService
    ) {
        this.unsubscribe$ = new Subject<void>();
    }

    ngOnInit(): void {
        this.getEmailUser();
        this.subscribePaymentMethods();
        this.isGuest = this.addPersonalForm;
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    responseAdyen(adyenResponse: AdyenComponentResponse | null): void {
        this.isLoading = false;
        switch (adyenResponse?.type) {
            case 'processing':
                this.isLoading = true;
                break;
            case 'completed':
                if (this.isGuest) {
                    this.paymentMethodsHandlerService.setGuestPaymentInformation({
                        creditCardForm: this.creditCardFormGroup.getRawValue(),
                    });
                }
                this.adyenCardAdded.emit({
                    adyenResponse,
                    creditCardFormGroup: this.isGuest ? this.creditCardFormGroup : undefined,
                });
                break;
            case 'error':
                if (adyenResponse.data === 'Personal information form is not completed') {
                    this.scrollToPersonalInfoForm('personalInfoForm');
                    this.creditCardFormGroup.markAllAsTouched();
                    return;
                }
                break;
        }
    }

    private getEmailUser(): void {
        this.emailUser = this.customerService.getCustomerEmail() || '';
    }

    private scrollToPersonalInfoForm(elementIdentifier: string): void {
        if (elementIdentifier === 'personalInfoForm' && this.personalInfoForm?.nativeElement) {
            this.personalInfoForm.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    private subscribePaymentMethods(): void {
        this.isLoadingForm = true;
        combineLatest([
            this.paymentMethodsService.getPaymentMethodsByType$(PaymentMethods.CREDIT_CARD),
            this.paymentMethodsService.getPaymentMethodsRequest$(),
        ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: ([paymentMethod, paymentMethods]) => {
                    if (paymentMethod && paymentMethod.processorName) {
                        this.processorName = paymentMethod.processorName;
                    }
                    this.isLoadingForm = false;
                },
                error: () => {
                    this.isLoadingForm = false;
                },
            });
    }

    creditCardFormGroupChange(creditCardFormGroup: CreditCardFormGroupType): void {
        this.creditCardFormGroup = creditCardFormGroup;
        this.isPersonalFormValid = this.creditCardFormGroup.valid;
    }
}
